/**
 * Service to handle stock data streaming from the backend.
 */
class StockStreamService {
    /**
     * Stream historical data for a symbol.
     * 
     * @param {string} symbol - Stock symbol (e.g., "RELIANCE")
     * @param {string} from - Start date in ISO sequence (e.g., "2023-01-01T00:00:00Z")
     * @param {string} to - End date in ISO sequence (e.g., "2023-12-31T23:59:59Z")
     * @param {string} interval - Candle interval (e.g., "1d")
     * @param {string} exchange - Exchange (default: "NSE")
     * @param {string} instrumentType - Instrument Type (default: "EQUITY")
     * @param {function} onMessage - Callback for when a new candle is received
     * @param {function} onError - Callback for errors
     * @param {function} onComplete - Callback for when the stream completes
     * @returns {function} Cleanup function to close the stream
     */
    streamHistoricalData(symbol, from, to, interval, exchange = 'NSE', instrumentType = 'EQUITY', onMessage, onError, onComplete) {
        const url = `/api/sat/algo/v1/stocks/${symbol}/history/stream?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&interval=${interval}&exchange=${exchange}&instrumentType=${instrumentType}`;

        console.log(`[StockStreamService] Connecting to ${url}`);

        const eventSource = new EventSource(url);

        eventSource.onopen = () => {
            console.log(`[StockStreamService] Connection opened for ${symbol}`);
        };

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                onMessage(data);
            } catch (error) {
                console.error('[StockStreamService] Error parsing message:', error);
                if (onError) onError(error);
            }
        };

        eventSource.onerror = (error) => {
            // Check if stream was explicitly closed or finished
            if (eventSource.readyState === 2) {
                console.log('[StockStreamService] Stream closed by server (Complete)');
                eventSource.close();
                if (onComplete) onComplete();
                return;
            }

            // Suppress error logging if it's just a connection reset/end of stream
            // Often EventSource fires an error event before closing when server finishes response
            console.log('[StockStreamService] Stream ended/interrupted (readyState=' + eventSource.readyState + ')');

            if (onError) onError(error);
            eventSource.close();
        };

        // Return cleanup function
        return () => {
            console.log(`[StockStreamService] Closing connection for ${symbol}`);
            eventSource.close();
        };
    }
}

export default new StockStreamService();
